import { FileDoneOutlined, FunnelPlotOutlined, LeftCircleOutlined, PicLeftOutlined, PictureOutlined, RightCircleOutlined } from '@ant-design/icons'
import { Button, Card, Select, Space } from 'antd'
import React, { useEffect, useState } from 'react'
import { AdGroupBean, AdvertiserBean, CampaignBean, CreativeBean, MediaAgencyBean, SspBean } from 'src/api/api'
import { useUser } from 'src/context/UserContext'
import { formDataLoader, FormDataType } from 'src/util/FormDataLoader'
import { isAdmin, isAgencyAdmin } from 'src/util/Util'
import './style.css'
import { DashboardFilters } from './useDashboardData'

const iconColor = '#6F757E'
const Option = Select.Option

interface FilterRowProps {
    icon: React.ReactNode
    children: React.ReactNode
    style?: React.CSSProperties
}

export const FilterRow: React.FC<FilterRowProps> = ({ icon, children, style }) => {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...style }}>
            <div style={{ width: 30, textAlign: 'center' }}>{icon}</div>
            {children}
        </div>
    )
}

export const emptyFilters = () => {
    return {
        ssp: [],
        mediaAgency: [],
        advertiser: [],
        campaign: [],
        adGroup: [],
        creative: [],
        countryCode: []
    }
}

const Filters: React.FC<{
    setFilters: (filters: DashboardFilters) => void
    filters: DashboardFilters
}> = ({ setFilters, filters }) => {

    const formInfoItems: FormDataType[] = [
        FormDataType.Advertisers,
        FormDataType.Campaigns,
        FormDataType.AdGroups,
        FormDataType.Creatives,
        FormDataType.Countries
    ]

    const { user } = useUser()

    const admin = isAdmin(user)
    const agencyAdmin = isAgencyAdmin(user)

    const [info, setInfo] = useState({
        ssps: [],
        mediaAgencies: [],
        campaigns: [],
        adGroups: [],
        creatives: [],
        advertisers: [],
        countries: []
    })

    const [selectedSsp, setSelectedSsp] = useState<string[]>(filters.ssp)
    const [selectedMediaAgency, setSelectedMediaAgency] = useState<string[]>(filters.mediaAgency)
    const [selectedAdvertiser, setSelectedAdvertiser] = useState<string[]>(filters.advertiser)
    const [selectedCampaign, setSelectedCampaign] = useState<string[]>(filters.campaign)
    const [selectedAdGroup, setSelectedAdGroup] = useState<string[]>(filters.adGroup)
    const [selectedCreative, setSelectedCreative] = useState<string[]>(filters.creative)
    const [selectedCountryCode, setSelectedCountryCode] = useState<string[]>(filters.countryCode)

    const handleApply = () => {
        const filter: DashboardFilters = {
            ssp: selectedSsp,
            mediaAgency: selectedMediaAgency,
            advertiser: selectedAdvertiser,
            campaign: selectedCampaign,
            adGroup: selectedAdGroup,
            creative: selectedCreative,
            countryCode: selectedCountryCode
        }
        setFilters(filter)
    }

    const handleClear = () => {

        setSelectedSsp([])
        setSelectedMediaAgency([])
        setSelectedAdvertiser([])
        setSelectedCampaign([])
        setSelectedAdGroup([])
        setSelectedCreative([])
        setSelectedCountryCode([])

        setFilters(emptyFilters())
    }

    const loadData = async () => {
        try {
            if (admin) formInfoItems.push(FormDataType.Ssps)
            if (admin || agencyAdmin) formInfoItems.push(FormDataType.MediaAgencies)

            const resp = await formDataLoader(...formInfoItems)

            setInfo(resp as any)
        } catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    return (
        <Card
            title="Filters"
            className="dashboard-filters-card"
            style={{ minWidth: '300px', height: 'fit-content' }}
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {admin && <FilterRow icon={<RightCircleOutlined style={{ color: iconColor }} />}>
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select SSP"
                        value={selectedSsp}
                        onChange={setSelectedSsp}
                    >
                        {info.ssps.map((item: SspBean) => <Option key={item.id} value={item.name}>{item.name}</Option>)}
                    </Select>
                </FilterRow>}
                {(admin || agencyAdmin) && <FilterRow icon={<FileDoneOutlined style={{ color: iconColor }} />}>
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select Media Agency"
                        value={selectedMediaAgency}
                        onChange={setSelectedMediaAgency}
                    >
                        {info.mediaAgencies.map((item: MediaAgencyBean) => <Option key={item.id} value={item.name}>{item.name}</Option>)}
                    </Select>
                </FilterRow>}
                <FilterRow icon={<LeftCircleOutlined style={{ color: iconColor }} />}>
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select Advertiser"
                        value={selectedAdvertiser}
                        onChange={setSelectedAdvertiser}
                    >
                        {info.advertisers.map((item: AdvertiserBean) => <Option key={item.id} value={item.name}>{item.name}</Option>)}
                    </Select>
                </FilterRow>
                <FilterRow icon={<FunnelPlotOutlined style={{ color: iconColor }} />}>
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select Campaign"
                        value={selectedCampaign}
                        onChange={setSelectedCampaign}
                    >
                        {info.campaigns.map((item: CampaignBean) => <Option key={item.id} value={item.name}>{item.name}</Option>)}
                    </Select>
                </FilterRow>
                <FilterRow icon={<PicLeftOutlined style={{ color: iconColor }} />}>
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select AdGroup"
                        value={selectedAdGroup}
                        onChange={setSelectedAdGroup}
                    >
                        {info.adGroups.map((item: AdGroupBean) => <Option key={item.id} value={item.name}>{item.name}</Option>)}
                    </Select>
                </FilterRow>
                <FilterRow icon={<PictureOutlined style={{ color: iconColor }} />}>
                    <Select
                        showSearch
                        allowClear
                        mode="multiple"
                        placeholder="Select Creative"
                        value={selectedCreative}
                        onChange={setSelectedCreative}
                    >
                        {info.creatives.map((item: CreativeBean) => <Option key={item.id} value={item.name}>{item.name}</Option>)}
                    </Select>
                </FilterRow>

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Button type="primary" ghost block onClick={handleApply} >
                        Show Results
                    </Button>
                    <Button style={{ border: 'transparent' }} block onClick={handleClear}>
                        Clear All
                    </Button>
                </Space>
            </Space>
        </Card>
    )
}

export default Filters


// WEBPACK FOOTER //
// ./src/component/app/dashboard/Filters.tsx